import { z } from 'zod';

/*
 * «Connect with Google» (owner decision 2026-11-03): the merchant signs in with their own Google
 * account once; Arabat verifies the store's main address, adds it to their Search Console and
 * submits its sitemap. No Google token is kept.
 */

/** `POST /manager/online/google/connect`: Google's consent page to open. */
export const googleConnectStartSchema = z.object({ url: z.url() });
export type GoogleConnectStart = z.infer<typeof googleConnectStartSchema>;

/** Why a connection failed: `?google=error&reason=…` on the console's settings page. */
export const GOOGLE_CONNECT_ERRORS = [
  'denied',
  'expired',
  'invalid_state',
  'not_published',
  'custom_domain_required',
  'unavailable',
  'verification_failed',
  'search_console_failed',
  'google_error',
] as const;
export type GoogleConnectError = (typeof GOOGLE_CONNECT_ERRORS)[number];

/** What Google sends back to the callback (every field optional: Google omits some on refusal). */
export const googleCallbackQuerySchema = z.object({
  state: z.string().max(2000).optional(),
  code: z.string().max(2000).optional(),
  error: z.string().max(200).optional(),
  scope: z.string().max(2000).optional(),
});

const named = { nameEn: z.string().nullable(), nameAr: z.string().nullable() };

/** Items of each list in the Google health check; the counts stay complete. */
export const GOOGLE_HEALTH_LIST_MAX = 50;
/** A description shorter than this (characters) is "too short" for Google. */
export const GOOGLE_SHORT_DESCRIPTION = 80;

/** A product or category named in a health list: the console links to it by id. */
export const googleHealthItemSchema = z.object({
  id: z.string(),
  ...named,
  handle: z.string().nullable(),
});
export type GoogleHealthItem = z.infer<typeof googleHealthItemSchema>;
/** How many, and the first {@link GOOGLE_HEALTH_LIST_MAX}. */
const healthList = z.object({ count: z.number().int(), items: z.array(googleHealthItemSchema) });

/**
 * `GET /manager/online/google/health`: the store's state in Google, computed with the same
 * builders as its public sitemap.xml and google-products.xml (a draft store: what they will hold
 * once it is published).
 */
export const googleHealthSchema = z.object({
  status: z.enum(['draft', 'published', 'paused']),
  /**
   * The store's own domain (owner decision 2026-11-04): every Google feature works only once it
   * is connected, working and the main address. `domain` is the connected one (working or not),
   * null when none. While `active` is false the other checks are not run (zeros, empty lists).
   */
  customDomain: z.object({ active: z.boolean(), domain: z.string().nullable() }),
  /** The store's main address (no trailing slash). */
  mainUrl: z.string(),
  /** A Google Search Console code is on the store's pages. */
  connected: z.boolean(),
  /** How: «Connect with Google» (an audited connection) or a pasted code; null = not linked. */
  connectedVia: z.enum(['button', 'manual']).nullable(),
  /** When «Connect with Google» last succeeded. */
  connectedAt: z.string().nullable(),
  /** Products shown on the website. */
  products: z.number().int(),
  /** Addresses in sitemap.xml. */
  sitemapPages: z.number().int(),
  /** Items (options) in google-products.xml, and the options left out. */
  feedItems: z.number().int(),
  feedSkipped: z.object({ noPicture: z.number().int() }),
  noPicture: healthList,
  /** No description, or one shorter than {@link GOOGLE_SHORT_DESCRIPTION} characters. */
  shortDescription: healthList,
  /** Neither a Google title nor a Google description (optional: automatic text is used). */
  noSeoText: healthList,
  /** Categories shown on the website without a Google description (optional). */
  categoriesNoSeo: healthList,
});
export type GoogleHealth = z.infer<typeof googleHealthSchema>;

/**
 * `GET /manager/online/google/check-product/:productId`: one product as Google sees it — the
 * search result the server's head tags make, its address, and whether the sitemap and the
 * Shopping file hold it.
 */
export const googleProductCheckSchema = z.object({
  productId: z.string(),
  ...named,
  /** Shown on the website (the console's switch); false = Google gets nothing for it. */
  visible: z.boolean(),
  handle: z.string().nullable(),
  /** The product page's main address; null when it is not shown. */
  url: z.string().nullable(),
  /** The result's title and description, exactly as the page's head tags carry them. */
  title: z.string().nullable(),
  description: z.string().nullable(),
  /** Whether they are the merchant's own Google text or made automatically. */
  titleSource: z.enum(['google', 'automatic']).nullable(),
  descriptionSource: z.enum(['google', 'automatic']).nullable(),
  inSitemap: z.boolean(),
  /** Options of the product in google-products.xml (0 = none). */
  feedItems: z.number().int(),
  /** Why the Shopping file leaves it out; null when it is in. */
  feedMissing: z.enum(['hidden', 'no_picture']).nullable(),
  /** The price told to Google: the lowest buyable price plus the required choices. */
  price: z.object({ amount: z.string(), currency: z.string() }).nullable(),
  /** Some option can be ordered now. */
  available: z.boolean(),
  hasPicture: z.boolean(),
  /** Characters of the description shoppers see. */
  descriptionLength: z.number().int(),
});
export type GoogleProductCheck = z.infer<typeof googleProductCheckSchema>;
